// Public Help Centre content. Screenshots live in /public/help/screens.
export const CATEGORIES = [
  { slug: "getting-started", title: "Getting started", desc: "Set up your clinic and find your way around.", icon: "Rocket" },
  { slug: "daily", title: "Daily operations", desc: "Bookings, clients, forms and clinical records.", icon: "CalendarDays" },
  { slug: "growth", title: "Grow your clinic", desc: "Retention, reviews, messaging and AI help.", icon: "TrendingUp" },
  { slug: "admin", title: "Admin & settings", desc: "Team, reporting, billing and integrations.", icon: "Settings" },
];

export const ARTICLES = [
  {
    slug: "welcome-getting-started",
    category: "getting-started",
    title: "Getting started with Azordi",
    summary: "A 10-minute setup checklist to get your clinic live — services, staff, hours and your booking page.",
    screenshot: "/help/screens/dashboard.png",
    readMins: 4,
    keywords: ["setup", "onboarding", "start", "first time", "dashboard", "trial"],
    intro: "Welcome! Azordi is your booking + business platform. Here's the fastest path from a fresh account to taking real bookings. Work top to bottom and you'll be live in about 10 minutes.",
    steps: [
      "Sign in and land on your Dashboard — this is your daily home base showing today's bookings, sales and 'what to act on'.",
      "Go to Setup → Services and add the treatments you offer, with price and duration. This is what clients pick when booking.",
      "Go to Setup → Staff and add your team, set their working days/hours and make them 'bookable'.",
      "Open Setup → Business Info to add your logo, address and contact details — these appear on your public booking page.",
      "Share your public booking link (yourclinic on /book/your-slug) so clients can book online 24/7.",
    ],
    tips: [
      "Your free trial banner at the top shows days remaining — add payment any time from Billing to keep going.",
      "The Dashboard 'What to act on' panel surfaces appointments awaiting checkout, lapsed clients and open tasks so nothing slips.",
    ],
  },
  {
    slug: "navigation-basics",
    category: "getting-started",
    title: "Finding your way around",
    summary: "How the sidebar, search and quick actions work so you can move fast.",
    screenshot: "/help/screens/dashboard.png",
    readMins: 2,
    keywords: ["navigation", "menu", "sidebar", "layout", "icons"],
    intro: "Azordi keeps a slim icon rail down the left so your screen stays focused on the work. Hover it to see full labels.",
    steps: [
      "The left rail shows an icon for every page. Hover over it and it expands to reveal labels grouped as Manage, Setup and Cost Tools.",
      "Move your mouse away and it collapses back to icons — giving you maximum room for your calendar and client records.",
      "On mobile, tap the menu button in the top bar to open the full navigation drawer.",
      "Your name, role and Sign out live at the bottom of the expanded menu.",
    ],
    tips: ["What you see in the menu depends on your role — owners and admins see everything; staff and reception see the pages relevant to them."],
  },
  {
    slug: "calendar-and-bookings",
    category: "daily",
    title: "Calendar & bookings",
    summary: "Create, move, reschedule and check out appointments — plus recurring bookings and CSV import.",
    screenshot: "/help/screens/calendar.png",
    readMins: 5,
    keywords: ["calendar", "booking", "appointment", "schedule", "recurring", "reschedule", "checkout", "import"],
    intro: "The Calendar is where your day happens. Each staff member gets a colour-coded column; drag, click and check out from one screen.",
    steps: [
      "Use Day / Week toggle and the arrows (or the date picker) to move around. 'Today' jumps back to now.",
      "Click 'New booking' to add an appointment — pick the client, service, staff member and time. Overlaps are blocked automatically.",
      "Click an existing booking to open it, where you can reschedule, cancel, take a deposit, or check the client out.",
      "To repeat a booking, turn on recurrence and choose the frequency and how many times (or an end date). Rescheduling shifts the future occurrences too.",
      "Have historical bookings elsewhere? Use 'Import' to bring in a CSV of appointments — clients, services and staff are matched automatically.",
    ],
    tips: [
      "A star or alert icon on a booking card flags VIPs and medical alerts before the client even arrives.",
      "Marking an appointment 'Arrived' is the safety checkpoint — medical alerts must be acknowledged here.",
    ],
  },
  {
    slug: "managing-clients",
    category: "daily",
    title: "Managing clients",
    summary: "Build client files, track history and credit, and import your existing client list.",
    screenshot: "/help/screens/clients.png",
    readMins: 4,
    keywords: ["client", "customer", "contact", "history", "credit", "import", "file"],
    intro: "Every client has a rich file: contact details, appointment history, treatment charts, forms, credit balance and packages.",
    steps: [
      "Open Clients and search by name or phone. Click a client to open their full file.",
      "Add a new client with the 'New client' button — capture their name, contact details and how they found you.",
      "Inside a client file you'll see tabs for their history, charts, forms, photos and account (credit & packages).",
      "Moving from another system? Use 'Import' to upload a CSV — duplicates and rows without a name are skipped automatically, and you get a summary of what imported.",
    ],
    tips: ["Client credit and pre-paid packages are tracked automatically and can be applied at checkout or rolled into a deposit."],
  },
  {
    slug: "consult-forms",
    category: "daily",
    title: "Consult forms",
    summary: "Send digital consultation & consent forms clients complete before their visit.",
    screenshot: "/help/screens/forms.png",
    readMins: 3,
    keywords: ["consult", "consultation", "form", "consent", "intake", "medical history"],
    intro: "Consult forms capture medical history and consent digitally, and feed straight into medical alerts so nothing important is missed.",
    steps: [
      "Open Consult Forms to see your form templates and any completed submissions.",
      "Send a form to a client — they receive a secure link (by SMS or shared directly) and complete it on their own device.",
      "Submitted forms are saved to the client's file, immutable, with a timestamp.",
      "Answers that indicate a risk (allergies, pregnancy, medications, etc.) automatically raise a Medical Alert on that client.",
    ],
    tips: ["Original submitted forms are never edited — they're a permanent record. New information creates new entries."],
  },
  {
    slug: "treatment-charts",
    category: "daily",
    title: "Treatment charts",
    summary: "Record clinical notes against each appointment, with templates, body maps and photos.",
    screenshot: "/help/screens/charts.png",
    readMins: 4,
    keywords: ["chart", "clinical", "notes", "treatment", "body map", "template", "laser", "injectable", "dermal"],
    intro: "Treatment charts are your clinical record — linked to the appointment, attributed to the practitioner, and kept as an append-only history.",
    steps: [
      "Set up reusable templates by service under Treatment Charts (e.g. Laser/Hair, Injectable, Dermal).",
      "From an appointment or the client's file, start a chart — the right template loads automatically for that service.",
      "Record settings and notes, drop pins on the 2D body map, and attach before/after photos to the chart.",
      "Need to repeat last visit's settings? Use 'Duplicate previous chart' and adjust.",
      "Every chart is timestamped and attributed, and past charts remain in the client's file chronologically.",
    ],
    tips: ["Charts are append-only for medico-legal safety — you add new charts rather than overwriting old ones."],
  },
  {
    slug: "medical-alerts",
    category: "daily",
    title: "Medical alerts",
    summary: "Automatic safety flags for allergies, pregnancy, medications and more — shown everywhere it matters.",
    screenshot: "/help/screens/clients.png",
    readMins: 3,
    keywords: ["medical", "alert", "safety", "allergy", "pregnancy", "contraindication", "clearance"],
    intro: "Medical alerts keep your team safe by surfacing red flags at every touchpoint — the client file, calendar card, treatment chart and checkout.",
    steps: [
      "Alerts are raised automatically from consult-form answers (allergies, isotretinoin, pregnancy/breastfeeding, blood thinners, adverse reactions, and more).",
      "You can also add manual conditions to a client's alert list at any time.",
      "When a client is marked 'Arrived', staff must acknowledge active alerts before proceeding — this is the primary safety block.",
      "Every acknowledgement is recorded with who and when, giving you an audit trail.",
    ],
    tips: ["Alerts appear on the calendar booking card and popup too, so you're warned before the appointment starts."],
  },
  {
    slug: "crm-retention",
    category: "growth",
    title: "CRM & client retention",
    summary: "Segment clients, assign follow-up tasks, and automatically win back lapsed clients.",
    screenshot: "/help/screens/crm.png",
    readMins: 4,
    keywords: ["crm", "retention", "segments", "vip", "lapsed", "win back", "tasks", "follow up", "rebooking", "campaign"],
    intro: "The CRM helps you keep clients coming back. It groups your clients into segments and gives you tools to act on each.",
    steps: [
      "Open CRM to see your New, Active, Lapsed and VIP segments. Click any segment to drill into the clients inside it.",
      "Create follow-up tasks, assign them to a team member and set a due date. Assignees see them in their dashboard's 'My follow-ups'.",
      "Send a rebooking or campaign SMS to a whole segment in a couple of clicks.",
      "Turn on automatic win-back to text lapsed clients a return offer — with built-in de-duplication so no one gets spammed.",
    ],
    tips: ["Win-back messages send when CRM activity runs, and each client is only messaged once per lapse to protect your brand."],
  },
  {
    slug: "surveys-and-reviews",
    category: "growth",
    title: "Surveys & Google reviews",
    summary: "Collect private feedback and route your happiest clients to leave a Google review.",
    screenshot: "/help/screens/surveys.png",
    readMins: 3,
    keywords: ["survey", "feedback", "review", "google", "nps", "kiosk", "rating"],
    intro: "The Feedback page has two tabs: Surveys (private feedback) and Google Reviews (public reputation). Happy clients get nudged to review; unhappy feedback stays private so you can fix it.",
    steps: [
      "Surveys auto-send by SMS after a client's third checked-out appointment — no manual work.",
      "Clients who score you highly (4–5/5) are shown your Google review link; lower scores stay private for your eyes only.",
      "Set your Google review link and score threshold in the Google Reviews tab.",
      "For in-clinic use, tap 'Open blank survey' to launch an anonymous kiosk survey in a new tab on your reception iPad.",
    ],
    tips: ["Abandoned kiosk surveys are excluded from your response-rate stats, so your numbers stay accurate."],
  },
  {
    slug: "sms-setup",
    category: "growth",
    title: "SMS setup",
    summary: "How clinic text messages work, top-ups, your sender name, and going live.",
    screenshot: "/help/screens/integrations.png",
    readMins: 4,
    keywords: ["sms", "text", "message", "reminder", "twilio", "sender", "credits", "notifications"],
    intro: "SMS powers reminders, survey invites, win-back and rebooking messages. It's built in — you don't need your own messaging account.",
    steps: [
      "Turn SMS on for your clinic and set your sender name (this is what clients see the text is from — up to 11 characters).",
      "Top up your SMS credit balance; each message is deducted at your per-message price.",
      "Messages send automatically as part of reminders, surveys and CRM campaigns once SMS is enabled and you have credit.",
      "Check the message log to see what was sent and its status.",
    ],
    tips: [
      "Real delivery depends on your region's sender registration being approved. Until then, messages are logged as 'simulated' so flows still work.",
      "If you run out of credit, sending is paused with a clear prompt to top up — nothing fails silently.",
    ],
  },
  {
    slug: "ai-assistant",
    category: "growth",
    title: "AI Assistant",
    summary: "Ask business questions, draft marketing copy and client replies, and save your processes.",
    screenshot: "/help/screens/assistant.png",
    readMins: 3,
    keywords: ["ai", "assistant", "claude", "chat", "marketing", "copy", "sop", "process", "help"],
    intro: "The Assistant is your AI copilot. It knows your live clinic data (for owners/admins) and helps with everything from numbers to newsletters.",
    steps: [
      "Open Assistant and type a question, or tap one of the suggestion chips to get started.",
      "Ask business questions ('how is revenue tracking this week?'), request marketing/SMS copy, draft client aftercare replies, or summarise treatment notes.",
      "Ask it to write a process (SOP) — then tap 'Save as process' on the reply to keep it in your Saved systems & processes panel.",
      "Delete a saved process any time from that panel.",
    ],
    tips: [
      "The Assistant is available to staff too, but business financials (revenue, sales, segment counts) are only shown to owners/admins.",
      "Saved processes you create as a staff member are private to you; owners and admins can see all clinic processes.",
    ],
  },
  {
    slug: "staff-and-permissions",
    category: "admin",
    title: "Staff & permissions",
    summary: "Add team members, set roles, working hours, pay and fine-grained access.",
    screenshot: "/help/screens/staff.png",
    readMins: 4,
    keywords: ["staff", "team", "roles", "permissions", "access", "roster", "pay", "wages", "profit share"],
    intro: "Each team member has a full staff file with Roster, Access and Pay tabs so you control exactly what they can do and see.",
    steps: [
      "Open Staff and add a team member with their role (owner, admin, reception or staff).",
      "In the Roster tab set their working days and hours — this drives their availability on the calendar.",
      "In the Access tab, grant or restrict fine-grained permissions like view dashboard, view reports, give discounts, edit clients and manage inventory.",
      "In the Pay tab, record wages and profit-share settings used in reporting.",
    ],
    tips: ["Owners and admins always have full access. For others, permissions layer on top of their role — great for giving one receptionist reporting access, for example."],
  },
  {
    slug: "reports",
    category: "admin",
    title: "Reports",
    summary: "Understand your business with executive, financial, appointment, client and staff reports.",
    screenshot: "/help/screens/reports.png",
    readMins: 3,
    keywords: ["reports", "analytics", "sales", "revenue", "kpi", "executive", "financial", "future value"],
    intro: "The Reports hub gives you a curated set of business, financial, appointment, client and staff reports — no spreadsheets required.",
    steps: [
      "Open Reports (Sales Report in the menu) to see reports grouped by category.",
      "Start with the Executive summary for appointments, sales and rebooking at a glance.",
      "Dig into Sales for takings by day, staff and payment method, or Future Value for revenue already booked ahead.",
      "Use the client and staff reports to see retention, capacity and utilisation.",
    ],
    tips: ["Reports respect permissions — only owners, admins or staff granted 'view reports' can open this section."],
  },
  {
    slug: "billing",
    category: "admin",
    title: "Billing & subscription",
    summary: "Manage your Azordi subscription, payment method and invoices.",
    screenshot: "/help/screens/billing.png",
    readMins: 2,
    keywords: ["billing", "subscription", "payment", "invoice", "trial", "stripe", "plan"],
    intro: "Billing is where you manage your Azordi subscription — separate from the payments your clinic takes from its own clients.",
    steps: [
      "Open Billing to see your current plan and trial status.",
      "Add or update your payment method to move from trial to a paid subscription.",
      "Manage your subscription and access invoices through the secure customer portal.",
    ],
    tips: ["Card payments are handled securely by Stripe — Azordi never stores your full card number."],
  },
  {
    slug: "integrations",
    category: "admin",
    title: "Integrations",
    summary: "Connect marketing tools like Mailchimp and Klaviyo to sync opted-in clients.",
    screenshot: "/help/screens/integrations.png",
    readMins: 3,
    keywords: ["integrations", "mailchimp", "klaviyo", "marketing", "sync", "email", "opt-in"],
    intro: "Integrations connect Azordi to your marketing tools so opted-in clients flow through automatically, with two-way unsubscribe respected.",
    steps: [
      "Open Integrations and choose the tool you want to connect (e.g. Mailchimp or Klaviyo).",
      "Enter your provider API key and pick the audience/list to sync into.",
      "Choose manual or automatic sync of your opted-in clients, with tags applied.",
      "Unsubscribes on the provider side flow back to Azordi and switch the client's opt-in off, keeping consent in sync.",
    ],
    tips: ["Only clients who have opted in to marketing are ever synced — consent is respected end to end."],
  },
  {
    slug: "marketing-tracking",
    category: "admin",
    title: "Marketing & tracking (Google & Meta Pixel)",
    summary: "Add your own Google Analytics, Google Ads and Meta (Facebook) Pixel so you can see which marketing turns into bookings.",
    screenshot: "/help/screens/marketing-tracking.png",
    readMins: 5,
    keywords: ["marketing", "tracking", "pixel", "meta", "facebook", "google", "analytics", "ga4", "gtm", "tag manager", "ads", "conversion", "utm"],
    intro: "Azordi can fire your own analytics and advertising tags on your public booking page, so visits and confirmed bookings show up in Google Analytics, Google Ads and Meta as conversions. You add your IDs once in Business info — Azordi installs the tags for you, no code required.",
    steps: [
      "In Azordi, open Setup → Business info and scroll to the 'Marketing & tracking' card (shown above).",
      "Paste any IDs you have: Google Analytics 4 Measurement ID (G-XXXXXXXX), Google Tag Manager Container ID (GTM-XXXXXXX), Meta (Facebook) Pixel ID (a 15–16 digit number) and/or Google Ads Conversion ID (AW-XXXXXXXX). Leave the rest blank.",
      "Click Save, then press Deploy (top right) — tags only go live on your booking page after a redeploy.",
      "To create a Meta Pixel: go to business.facebook.com/events_manager → Connect data source → Web, name it (e.g. 'Azordi – Your Clinic') and choose 'Install code manually' (Azordi installs it, so you can close the code screen). Copy the Pixel ID and paste it into the card above.",
      "To create a GA4 ID: go to analytics.google.com → Admin → Data streams → add a Web stream for your booking URL, then copy the 'Measurement ID' (G-XXXXXXXX).",
      "Find your booking-page URL under Setup → Settings (shown below) — that's the page the tags track and the URL you enter into Meta/Google.",
      "For campaigns, add a source tag to the link you share, e.g. .../book/your-clinic?utm_source=instagram — Azordi uses this to attribute the visit/booking in its own Reports → Booking conversions.",
    ],
    shots: [
      { src: "/help/screens/booking-link.png", caption: "Settings → Online booking link: copy your public booking link — this is the page your tags track and the URL you register in Meta/Google." },
    ],
    tips: [
      "Azordi automatically fires a PageView on the booking page and a 'Schedule' conversion when a client confirms a booking — no extra setup.",
      "Turning on Meta's 'Automatic Advanced Matching' shares hashed contact details for better attribution — only do this because our Privacy policy discloses it; consider a consent notice too.",
      "You can watch it all in one place under Reports → Booking conversions, broken down by traffic source.",
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
export const articlesByCategory = (cat) => ARTICLES.filter((a) => a.category === cat);
